import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  processItemSetupValidations,
  processSectionsToFieldNames,
} from '../../services/products-service'
import { ProductsState } from '../../models/store/products/ProductsState'
import {
  getItemBps,
  getProducts,
  getProductsForISU,
  getDeletedProducts,
  getExcelSheet,
  getItemSetupCount,
  bulkUpdateProducts,
  getISUValidations,
  saveProducts,
  copyProducts,
  cloneProducts,
  getAuditHighlights,
  searchPotentialItems,
  updateItemBpsGroup,
  getItemByItemId,
} from '../../services/products-service'
import { AxiosPromise } from 'axios'
import {
  ProductsResponse,
  mergeProductsResponses,
} from '../../models/products/productsResponse'
import { Products, mergeProducts } from '../../models/products/products'
import { RootState } from 'store/store'

export const fetchAllItemBpsAction = createAsyncThunk(
  'products/fetchAllItemBps',
  async (data: { eventIds; bpIds; page; perPage; requestBody }) => {
    return await getItemBps({
      ...data,
      itemLevelOnly: true,
      fetchAll: true,
    })
  },
)

export const fetchProductsAction = createAsyncThunk<
  any,
  any,
  { state: RootState }
>(
  'products/fetchProducts',
  async (
    data: {
      eventId
      bidId
      userType
      viewId
      isDeleted
      page
      perPage
      requestBody
      isApparelEvent
      isStaticView
      screenName
      bpViewIds
      fetchAll
    },
    { getState }: { getState: () => RootState },
  ) => {
    const {
      page = 1,
      perPage = 1000, // max
      bpViewIds = [],
      fetchAll = false,
    } = data

    const bpId = getState().auth.partnerId

    const params = {
      ...data,
      page,
      perPage,
      bpViewIds,
      bpId,
    }

    const res = await getProducts(params)

    if (!fetchAll) {
      return res.data
    } else {
      // Since we can only get 1000 top-level products at a time (even if the number of products returned is much larger)
      // we have to get the initial response, with the count, then request the rest, merging the responses
      const promises: AxiosPromise<ProductsResponse>[] = []
      const count = res?.data?.products?.totalItemsIncludingLevels ?? 0
      // res?.data?.products?.count > 0
      //   ? res?.data?.products?.count
      //   : res?.data?.products?.totalItemsIncludingLevels ?? 0
      for (
        let requestPage = 1;
        requestPage * res.data.products.per_page < count;
        requestPage++
      ) {
        promises.push(
          getProducts({
            ...params,
            page: requestPage + 1,
          }),
        )
      }

      const results = await Promise.all(promises)

      const finalResponse = results.reduce((accumulatorResponse, response) => {
        return mergeProductsResponses(accumulatorResponse, response.data)
      }, res.data)

      return finalResponse
    }
  },
)

export const fetchProductsForISUAction = createAsyncThunk<
  any,
  any,
  { state: RootState }
>(
  'products/fetchProductsForISUA',
  async (
    data: {
      eventId
      bpIds
      userType
      viewId
      isDeleted
      requestBody
      isApparelEvent
      isuFlag
    },
    { getState }: { getState: () => RootState },
  ) => {
    const bpId = getState().auth.partnerId

    const page = 1
    const perPage = 1000 // max products fetch at a time.

    const params = {
      ...data,
      page,
      perPage,
      bpId,
    }

    const res = await getProductsForISU(params)

    // Since we can only get 1000 top-level products at a time (even if the number of products returned is much larger)
    // we have to get the initial response, with the count, then request the rest, merging the responses
    const promises: AxiosPromise<ProductsResponse>[] = []
    const count = res?.data?.products?.totalItemsIncludingLevels ?? 0
    for (
      let requestPage = 1;
      requestPage * res.data.products.per_page < count;
      requestPage++
    ) {
      promises.push(
        getProductsForISU({
          ...params,
          page: requestPage + 1,
        }),
      )
    }

    const results = await Promise.all(promises)

    const finalResponse = results.reduce((accumulatorResponse, response) => {
      return mergeProductsResponses(accumulatorResponse, response.data)
    }, res.data)

    return finalResponse
  },
)

export const fetchDeletedProductsAction = createAsyncThunk(
  'products/fetchDeletedProducts',
  async (data: {
    eventId
    bpIds
    isDeleted
    requestBody
    isApparelEvent
    screenName
  }) => {
    const page = 1
    const perPage = 1000 // max at a time

    const params = {
      ...data,
      page,
      perPage,
    }

    const res = await getDeletedProducts(params)

    // Since we can only get 1000 top-level products at a time (even if the number of products returned is much larger)
    // we have to get the initial response, with the count, then request the rest, merging the responses
    const promises: Promise<Products>[] = []
    const count = res?.totalItemsIncludingLevels ?? 0
    for (
      let requestPage = 1;
      requestPage * res.per_page < count;
      requestPage++
    ) {
      promises.push(
        getDeletedProducts({
          ...params,
          page: requestPage + 1,
        }),
      )
    }

    const results = await Promise.all(promises)

    const finalResponse = results.reduce((accumulatorResponse, response) => {
      return mergeProducts(accumulatorResponse, response)
    }, res)

    return finalResponse
  },
)

export const fetchDeletedProductsCountAction = createAsyncThunk(
  'products/fetchDeletedProductsCount',
  async (data: {
    eventId
    bpIds
    isDeleted
    requestBody
    isApparelEvent
    screenName
  }) => {
    const page = 1
    const perPage = 1

    const params = {
      ...data,
      page,
      perPage,
    }

    return await getDeletedProducts(params)
  },
)

export const fetchItemSetupCount = createAsyncThunk<
  any,
  any,
  { state: RootState }
>(
  'products/fetchItemSetupCount',
  async (eventObjectId, { getState }: { getState: () => RootState }) => {
    const bpId = getState().auth.partnerId
    return await getItemSetupCount(eventObjectId, bpId)
  },
)

export const bulkUpdateProductsAction = createAsyncThunk<
  any,
  { eventId; bpIds; requestBody },
  { state: RootState }
>(
  'products/bulkUpdateProducts',
  async (
    data: { eventId; bpIds; requestBody },
    { getState }: { getState: () => RootState },
  ) => {
    const { eventId, bpIds, requestBody } = data
    const bpId = bpIds ?? getState().auth.partnerId
    return await bulkUpdateProducts(eventId, bpId, requestBody)
  },
)

export const saveProductsAction = createAsyncThunk(
  'products/saveProducts',
  async (
    args: {
      data
      vendorId
      bidObjectId
      eventId
      vendorType?
      isDynamicComponentOrMaster?
      fastTrackEvent?
    },
    { rejectWithValue },
  ) => {
    const {
      vendorType = [],
      isDynamicComponentOrMaster = false,
      fastTrackEvent = false,
    } = args
    try {
      return await saveProducts({
        ...args,
        vendorType,
        isDynamicComponentOrMaster,
        fastTrackEvent,
      })
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error)
      }
      return rejectWithValue('Save failed due to unknown error')
    }
  },
)

export const copyProductsAction = createAsyncThunk(
  'products/copyProducts',
  async (args: { eventId; bpIds; data }) => {
    const { eventId, bpIds, data } = args
    return await copyProducts(eventId, bpIds, data)
  },
)

export const cloneProductsAction = createAsyncThunk(
  'products/cloneProducts',
  async (args: { eventId; bpIds; data }) => {
    const { eventId, bpIds, data } = args
    return await cloneProducts(eventId, bpIds, data)
  },
)

export const fetchISUValidations = createAsyncThunk(
  'products/fetchISUValidations',
  async (data: { bpId; itemBpIds }) => {
    const { bpId, itemBpIds } = data
    const requestBody = { item_bps: itemBpIds ?? [] }
    return await getISUValidations(bpId, requestBody)
  },
)

export const getExcelSheetAction = createAsyncThunk(
  'products/getExcelSheet',
  async (args: {
    eventId
    bpIds
    isDeleted
    page
    perPage
    isuFlag
    requestBody
    fileName
    screenName
    isApparelEvent
  }) => {
    return await getExcelSheet(args)
  },
)

export const fetchAllImpressionsForProgramAction = createAsyncThunk(
  'products/fetchAllImpressionsForProgram',
  async (args: { eventId; bpIds; programName; currentPage; perPage }) => {
    const { eventId, bpIds, programName, currentPage = 1, perPage = 25 } = args

    const programNameArr: any[] = []
    programNameArr.push(programName)
    const filterSortRequest = {
      filter_request: [
        { field: 'expose_to_bp', values: [true], operator: 'equals' },
        { field: 'level', values: ['IMPRESSION'], operator: 'equals' },
        { field: 'level_group', values: programNameArr, operator: 'equals' },
        { field: 'apex_id', values: [null], operator: 'equals' },
      ],
      sort_request: [],
    }

    return await getItemBps({
      eventIds: [eventId],
      bpIds,
      page: currentPage,
      perPage,
      requestBody: filterSortRequest,
    })
  },
)

export const fetchAuditHighlightsAction = createAsyncThunk(
  'products/fetchAuditHighlights',
  async (args: { eventId; requestBody }) => {
    const { eventId, requestBody } = args
    return await getAuditHighlights(eventId, requestBody)
  },
)

export const searchPotentialItemsAction = createAsyncThunk(
  'products/searchPotentialItems',
  async (args: { eventId; page?; perPage?; requestBody? }) => {
    const {
      eventId,
      page = 1,
      perPage = 1000,
      requestBody = { filter_request: [], sort_request: [] },
    } = args

    return await searchPotentialItems({
      eventId,
      page,
      perPage,
      requestBody,
    })
  },
)

export const updateItemBpGroupAction = createAsyncThunk(
  'products/updateItemBpGroup',
  async (args: { updateItemBpGroupRequest; drillToImpressions }) => {
    const { updateItemBpGroupRequest, drillToImpressions } = args
    return await updateItemBpsGroup(
      updateItemBpGroupRequest,
      drillToImpressions,
    )
  },
)

export const fetchItemsByIdAction = createAsyncThunk(
  'products/fetchItemsById',
  async (itemId: string) => {
    return await getItemByItemId(itemId)
  },
)

const initialState: ProductsState = {
  itemBps: [],
  products: [],
  isFetchingItemSetupCount: false,
  itemSetupCount: -1,
  isFetchingItemBps: false,
  isFetchingProducts: false,
  isFetchingDeletedProductsCount: false,
  productList: [],
  productsPage: 1,
  productsPageCount: 100,
  productsTotalCount: 0,
  productsTotalLevelCount: 0,
  sections: [],
  views: [],
  fieldNames: {},
  businessConfirmationView: [],
  isBulkUpdating: false,
  isuValidationsList: [],
  alertList: [],
  isFetchingISUValidations: false,
  productsDeptSubClazz: {},
  parentItemBpIds: {},
  isSavingProducts: false,
  saveProductsErrors: null,
  isFetchingDeletedProducts: false,
  deletedProductsCount: 0,
  deletedProductsLevelCount: 0,
  deletedProductList: [],
  isCopyProducts: false,
  isCloneProducts: false,
  isFetchingAuditHighlights: false,
  audits: {},
  pmuRules: {},
  readOnlyAttributesForCF: [],
  productFeedbackHighlights: {},
  isSearchingPotentialItems: false,
  potentialItemsList: [],
  impressionList: [],
  impressionPageCount: 0,
  impressionTotalCount: 0,
  impressionTotalLevelCount: 0,
  itemSetupView: {},
  isFetchingAllItemBps: false,
  isFetchingAllImpressionsForProgram: false,
}

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    resetProductsAction: state => {
      state.productList = []
      state.productsPage = 1
      state.productsPageCount = 100
      state.productsTotalCount = 0
      state.productsTotalLevelCount = 0
      state.sections = []
      state.fieldNames = {}
      state.businessConfirmationView = []
      state.isuValidationsList = []
      state.alertList = []
      state.productsDeptSubClazz = {}
      state.parentItemBpIds = {}
      state.saveProductsErrors = null
      state.views = []
      state.productFeedbackHighlights = {}
    },
    resetItemBpsAction: state => {
      state.itemBps = []
    },
    resetISUValidationAction: state => {
      state.isuValidationsList = []
    },
    resetAuditsAction: state => {
      state.audits = {}
    },
  },
  extraReducers: builder => {
    builder

      // fetchProductsAction
      .addCase(fetchProductsAction.pending, state => {
        state.isFetchingProducts = true
      })
      .addCase(fetchProductsAction.fulfilled, (state, action) => {
        const {
          products,
          view,
          sections,
          feedbackHighlights,
          productsDeptSubClazz,
          parentItemBpIds,
          pmuRules,
          readOnlyAttributesForCF,
        } = action.payload
        const fieldNames = processSectionsToFieldNames(sections)

        state.isFetchingProducts = false
        state.productList = products.values
        state.productsPage = products.page
        state.productsPageCount = products.per_page
        state.productsTotalCount = products.count
        state.productsTotalLevelCount = products.totalItemsIncludingLevels
        state.sections = sections
        state.fieldNames = fieldNames
        state.businessConfirmationView = view.find(
          res =>
            res.viewName === 'Vendor Alignment View' &&
            res.createUser === 'PCNSystemUser',
        )
        state.views = view
        state.productFeedbackHighlights = feedbackHighlights
        state.productsDeptSubClazz = productsDeptSubClazz
        state.parentItemBpIds = parentItemBpIds
        state.pmuRules = pmuRules
        state.readOnlyAttributesForCF = readOnlyAttributesForCF
      })
      .addCase(fetchProductsAction.rejected, state => {
        state.isFetchingProducts = false
      })

      // fetchAllImpressionsForProgramAction
      .addCase(fetchAllImpressionsForProgramAction.pending, state => {
        state.isFetchingAllImpressionsForProgram = true
        state.impressionList = []
        state.impressionPageCount = 0
        state.impressionTotalCount = 0
        state.impressionTotalLevelCount = 0
      })
      .addCase(
        fetchAllImpressionsForProgramAction.fulfilled,
        (state, action) => {
          const { payload } = action

          state.isFetchingAllImpressionsForProgram = false
          state.impressionList = payload.values
          state.impressionPageCount = payload.per_page
          state.impressionTotalCount = payload.count
          state.impressionTotalLevelCount = payload.totalItemsIncludingLevels
        },
      )
      .addCase(fetchAllImpressionsForProgramAction.rejected, state => {
        state.isFetchingAllImpressionsForProgram = false
        state.impressionList = []
        state.impressionPageCount = 0
        state.impressionTotalCount = 0
        state.impressionTotalLevelCount = 0
      })

      // fetchProductsForISUAction
      .addCase(fetchProductsForISUAction.pending, state => {
        state.isFetchingProducts = true
      })
      .addCase(fetchProductsForISUAction.fulfilled, (state, action) => {
        const {
          products,
          sections,
          productsDeptSubClazz,
          parentItemBpIds,
          view,
        } = action.payload
        const fieldNames = processSectionsToFieldNames(sections)

        state.isFetchingProducts = false
        state.productList = products.values
        state.productsPage = products.page
        state.productsPageCount = products.per_page
        state.productsTotalCount = products.count
        state.productsTotalLevelCount = products.totalItemsIncludingLevels
        state.sections = sections
        state.fieldNames = fieldNames
        state.productsDeptSubClazz = productsDeptSubClazz
        state.parentItemBpIds = parentItemBpIds
        state.saveProductsErrors = null
        state.itemSetupView = view.find(
          res =>
            res.viewName === 'Item Setup View' &&
            res.createUser === 'PCNSystemUser',
        )
      })
      .addCase(fetchProductsForISUAction.rejected, state => {
        state.isFetchingProducts = false
      })

      // fetchDeletedProductsAction
      .addCase(fetchDeletedProductsAction.pending, state => {
        state.isFetchingDeletedProducts = true
      })
      .addCase(fetchDeletedProductsAction.fulfilled, (state, action) => {
        //This is required for the row count status panel
        const deletedProductListTemp = action.payload.values
        if (deletedProductListTemp?.length > 0) {
          deletedProductListTemp[0].totalRowCount =
            deletedProductListTemp.length
        }

        state.isFetchingDeletedProducts = false
        state.deletedProductList = deletedProductListTemp
        state.productsPage = action.payload.page
        state.productsPageCount = action.payload.per_page
        state.deletedProductsCount = action.payload.count
        state.deletedProductsLevelCount =
          action.payload.totalItemsIncludingLevels
      })
      .addCase(fetchDeletedProductsAction.rejected, state => {
        state.isFetchingDeletedProducts = false
      })

      // fetchDeletedProductsCountAction
      .addCase(fetchDeletedProductsCountAction.pending, state => {
        state.isFetchingDeletedProductsCount = true
      })
      .addCase(fetchDeletedProductsCountAction.fulfilled, (state, action) => {
        state.isFetchingDeletedProductsCount = false
        state.deletedProductsCount = action.payload.count
        state.deletedProductsLevelCount =
          action.payload.totalItemsIncludingLevels
      })
      .addCase(fetchDeletedProductsCountAction.rejected, state => {
        state.isFetchingDeletedProductsCount = false
      })

      // fetchAuditHighlightsAction
      .addCase(fetchAuditHighlightsAction.pending, state => {
        state.isFetchingAuditHighlights = true
      })
      .addCase(fetchAuditHighlightsAction.fulfilled, (state, action) => {
        state.isFetchingAuditHighlights = false
        state.audits = action.payload.data.values
      })
      .addCase(fetchAuditHighlightsAction.rejected, state => {
        state.isFetchingAuditHighlights = false
      })

      // fetchItemSetupCount
      .addCase(fetchItemSetupCount.pending, state => {
        state.isFetchingItemSetupCount = true
      })
      .addCase(fetchItemSetupCount.fulfilled, (state, action) => {
        const count = action.payload.data.count

        state.isFetchingItemSetupCount = false
        state.itemSetupCount = count
      })
      .addCase(fetchItemSetupCount.rejected, state => {
        state.isFetchingItemSetupCount = false
        state.itemSetupCount = -1
      })

      // bulkUpdateProductsAction
      .addCase(bulkUpdateProductsAction.pending, state => {
        state.isBulkUpdating = true
      })
      .addCase(bulkUpdateProductsAction.fulfilled, state => {
        state.isBulkUpdating = false
      })
      .addCase(bulkUpdateProductsAction.rejected, state => {
        state.isBulkUpdating = false
      })

      // fetchISUValidations
      .addCase(fetchISUValidations.pending, state => {
        state.isFetchingISUValidations = true
      })
      .addCase(fetchISUValidations.fulfilled, (state, action) => {
        const { payload } = action

        const [isuValidationsList, alertList] = processItemSetupValidations(
          payload.data.values,
          state.productList,
          state.fieldNames,
        )

        state.isuValidationsList = isuValidationsList
        state.alertList = alertList
        state.isFetchingISUValidations = false
      })
      .addCase(fetchISUValidations.rejected, state => {
        state.isFetchingISUValidations = false
      })

      // saveProductsAction
      .addCase(saveProductsAction.pending, state => {
        state.isSavingProducts = true
      })
      .addCase(saveProductsAction.fulfilled, state => {
        state.isSavingProducts = false
        state.saveProductsErrors = null
      })
      .addCase(saveProductsAction.rejected, (state, action) => {
        const payload: any = action.payload
        const hasErrors = Object.keys(payload.response.data).length > 0

        state.isSavingProducts = false
        state.saveProductsErrors = hasErrors ? payload.response.data : null
      })

      // copyProductsAction
      .addCase(copyProductsAction.pending, state => {
        state.isCopyProducts = true
      })
      .addCase(copyProductsAction.fulfilled, state => {
        state.isCopyProducts = false
      })
      .addCase(copyProductsAction.rejected, state => {
        state.isCopyProducts = false
      })

      // cloneProductsAction
      .addCase(cloneProductsAction.pending, state => {
        state.isCloneProducts = true
      })
      .addCase(cloneProductsAction.fulfilled, state => {
        state.isCloneProducts = false
      })
      .addCase(cloneProductsAction.rejected, state => {
        state.isCloneProducts = false
      })

      // searchPotentialItemsAction
      .addCase(searchPotentialItemsAction.pending, state => {
        state.isSearchingPotentialItems = true
        state.potentialItemsList = []
      })
      .addCase(searchPotentialItemsAction.fulfilled, (state, action) => {
        const { payload } = action

        state.isSearchingPotentialItems = false
        state.potentialItemsList = payload.data.values
      })
      .addCase(searchPotentialItemsAction.rejected, state => {
        state.isSearchingPotentialItems = false
      })

      // fetchAllItemBpsAction
      .addCase(fetchAllItemBpsAction.pending, state => {
        state.isFetchingAllItemBps = true
      })
      .addCase(fetchAllItemBpsAction.fulfilled, (state, action) => {
        state.itemBps = action.payload.values
        state.isFetchingAllItemBps = false
      })
      .addCase(fetchAllItemBpsAction.rejected, state => {
        state.itemBps = []
        state.isFetchingAllItemBps = false
      })
  },
})

export const getDeletedProductsList = state => state.products.deletedProductList
export const getIsFetchingDeletedProducts = state =>
  state.products.isFetchingDeletedProducts
export const getDeletedProductsCount = state =>
  state.products.deletedProductsCount
export const getDeletedProductsLevelCount = state =>
  state.products.deletedProductsLevelCount
export const getProductsList = state => state.products.productList
export const getAudits = state => state.products.audits

export const {
  resetProductsAction,
  resetItemBpsAction,
  resetISUValidationAction,
  resetAuditsAction,
} = productsSlice.actions

export default productsSlice.reducer
